import React from 'react'
import { Form, InputNumber, Spin, Switch, Table } from 'antd'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import { getShopConfig, loadMemberSettingConfig, saveMemberSettingConfig, updateShopConfig } from '../api'
import SettingPage, {
    SettingActions,
    SettingGrid,
    SettingInputNumber,
    SettingPanel,
    SettingTableContainer,
    numberOrUndefined,
} from './setting-page'
import { validateRechargeBonusLevels } from './recharge-setting-utils'
import { useMemberSettingAccess } from './setting-access'

import './recharge-setting.scss'

interface RechargeForm {
    activity_id: number
    levels: Array<{ threshold?: number; bonus_amount?: number }>
    custom_recharge_on: boolean
    recharge_minimum_amount?: number
    balance_sufficient: boolean
    balance_deduction_rate?: number
}

const formatMoney = (value?: number) => Number(value || 0).toFixed(2)

const RechargeSetting: React.FC = () => {
    const [form] = Form.useForm<RechargeForm>()
    const access = useMemberSettingAccess()

    const loadReq = useRequest(
        async () => {
            const [activity, shopConfig] = await Promise.all([
                loadMemberSettingConfig('/activity/getrechargebonusactivity'),
                getShopConfig(),
            ])

            return { activity, shopConfig }
        },
        {
            onSuccess: res => {
                form.setFieldsValue({
                    activity_id: res?.activity?.activity_id,
                    levels: (res?.activity?.levels || []).map((item: any) => ({
                        threshold: Number(item.threshold || 0),
                        bonus_amount: Number(item.bonus_amount || 0),
                    })),
                    custom_recharge_on: !!res?.shopConfig?.custom_recharge_on,
                    recharge_minimum_amount: Number(res?.shopConfig?.recharge_minimum_amount || 0),
                    balance_sufficient: !!res?.shopConfig?.balance_sufficient,
                    balance_deduction_rate: Number(res?.shopConfig?.balance_deduction_rate || 1) * 100,
                })
            },
            onError: e => $msg('error', e.message || '获取余额充值设置失败'),
        },
    )

    const saveReq = useRequest(
        async (values: RechargeForm) => {
            await updateShopConfig({
                custom_recharge_on: values.custom_recharge_on,
                recharge_minimum_amount: Number(values.recharge_minimum_amount || 0),
                balance_sufficient: values.balance_sufficient,
                balance_deduction_rate: Number(values.balance_deduction_rate || 0) / 100,
            })
            await saveMemberSettingConfig('/activity/updaterechargebonus', {
                activity_id: values.activity_id,
                levels: (values.levels || []).map(item => ({
                    threshold: numberOrUndefined(item.threshold),
                    bonus_amount: numberOrUndefined(item.bonus_amount),
                })),
            })
        },
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '保存成功')
                loadReq.refresh()
            },
            onError: e => $msg('error', e.message || '保存余额充值设置失败'),
        },
    )

    const onSubmit = async () => {
        const values = await form.validateFields()
        const levels = values.levels || []
        if (values.custom_recharge_on && Number(values.recharge_minimum_amount || 0) < 0) {
            $msg('warning', '最低充值金额不能小于0')

            return
        }
        if (
            values.balance_deduction_rate === undefined ||
            Number(values.balance_deduction_rate) < 1 ||
            Number(values.balance_deduction_rate) > 100
        ) {
            $msg('warning', '每单余额抵扣上限请输入1%到100%')

            return
        }
        if (!levels.length) {
            $msg('warning', '请至少保留一个充值赠送档位')

            return
        }
        for (let index = 0; index < levels.length; index++) {
            const level = levels[index]
            if (level.threshold === undefined || level.bonus_amount === undefined) {
                $msg('warning', '请填写完整的充值金额和赠送余额')

                return
            }
            if (Number(level.threshold) <= 0) {
                $msg('warning', '充值金额必须大于0')

                return
            }
            if (Number(level.bonus_amount) <= 0) {
                $msg('warning', '赠送余额必须大于0')

                return
            }
            if (index > 0 && Number(level.threshold) <= Number(levels[index - 1].threshold)) {
                $msg('warning', '充值档位金额需要按从小到大递增')

                return
            }
        }
        const bonusLevelError = validateRechargeBonusLevels(levels)
        if (bonusLevelError) {
            $msg('warning', bonusLevelError)

            return
        }
        saveReq.run(values)
    }

    return (
        <SettingPage title="余额/充值设置" className="member-recharge-setting" readOnly={access.readOnly}>
            <Spin spinning={loadReq.loading}>
                <Form form={form} layout="vertical">
                    <section className="member-recharge-setting__overview">
                        <div className="member-recharge-setting__overview-heading">
                            <div className="member-recharge-setting__overview-title">充值活动概览</div>
                            <div className="member-recharge-setting__overview-desc">累计经营数据</div>
                        </div>
                        <div className="member-recharge-setting__overview-stats">
                            <div className="member-recharge-setting__stat">
                                <div className="member-recharge-setting__stat-label">参与人数</div>
                                <div className="member-recharge-setting__stat-value">
                                    {loadReq.data?.activity?.participated_person || 0}
                                    <span>人</span>
                                </div>
                            </div>
                            <div className="member-recharge-setting__stat">
                                <div className="member-recharge-setting__stat-label">引导充值</div>
                                <div className="member-recharge-setting__stat-value">
                                    {formatMoney(loadReq.data?.activity?.guided_recharge)}
                                    <span>元</span>
                                </div>
                            </div>
                        </div>
                    </section>
                    <SettingGrid className="member-recharge-setting__layout">
                        <SettingPanel title="充值规则" desc="设置顾客可以充值的金额范围。">
                            <Form.Item name="custom_recharge_on" label="允许任意金额充值" valuePropName="checked">
                                <Switch checkedChildren="允许" unCheckedChildren="仅限档位" />
                            </Form.Item>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, next) => prev.custom_recharge_on !== next.custom_recharge_on}
                            >
                                {() =>
                                    form.getFieldValue('custom_recharge_on') ? (
                                        <Form.Item name="recharge_minimum_amount" label="最低充值金额">
                                            <SettingInputNumber min={0} precision={2} addonAfter="元" />
                                        </Form.Item>
                                    ) : null
                                }
                            </Form.Item>
                        </SettingPanel>
                        <SettingPanel title="余额使用规则" desc="设置每笔订单使用余额时的限制。">
                            <Form.Item name="balance_sufficient" label="余额不足时禁止部分抵扣" valuePropName="checked">
                                <Switch checkedChildren="禁止" unCheckedChildren="允许" />
                            </Form.Item>
                            <Form.Item name="balance_deduction_rate" label="每单余额抵扣上限">
                                <SettingInputNumber min={1} max={100} precision={0} addonAfter="%" />
                            </Form.Item>
                        </SettingPanel>
                        <Form.Item name="activity_id" hidden>
                            <InputNumber />
                        </Form.Item>
                        <Form.List name="levels">
                            {(fields, { add, remove }) => {
                                const columns = [
                                    {
                                        title: '充值金额',
                                        render: (_: any, field: any) => (
                                            <Form.Item name={[field.name, 'threshold']} noStyle>
                                                <SettingInputNumber min={0} precision={2} addonAfter="元" />
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: '赠送余额',
                                        render: (_: any, field: any) => (
                                            <Form.Item name={[field.name, 'bonus_amount']} noStyle>
                                                <SettingInputNumber min={0} precision={2} addonAfter="元" />
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: '操作',
                                        width: 112,
                                        render: (_: any, field: any) =>
                                            fields.length > 1 ? (
                                                <span className="action danger" onClick={() => remove(field.name)}>
                                                    删除
                                                </span>
                                            ) : (
                                                <span className="member-recharge-setting__only-level">
                                                    至少保留一档
                                                </span>
                                            ),
                                    },
                                ]

                                return (
                                    <SettingPanel
                                        title="充值赠送档位"
                                        desc="顾客充值达到对应金额后，系统自动赠送余额。"
                                        className="member-recharge-setting__levels"
                                        wide
                                        extra={
                                            <Button
                                                types="primary"
                                                className="member-recharge-setting__add-level"
                                                onClick={() => add({ threshold: 0, bonus_amount: 0 })}
                                            >
                                                新增档位
                                            </Button>
                                        }
                                    >
                                        <SettingTableContainer>
                                            <Table
                                                rowKey="key"
                                                columns={columns}
                                                dataSource={fields}
                                                pagination={false}
                                            />
                                        </SettingTableContainer>
                                        <SettingActions>
                                            <Auth perssion={20} onClick={onSubmit}>
                                                <Button
                                                    types="default"
                                                    loading={saveReq.loading}
                                                    disabled={saveReq.loading}
                                                >
                                                    保存
                                                </Button>
                                            </Auth>
                                        </SettingActions>
                                    </SettingPanel>
                                )
                            }}
                        </Form.List>
                    </SettingGrid>
                </Form>
            </Spin>
        </SettingPage>
    )
}

export default RechargeSetting
